import type {
  TranscriptMinuteBlock,
  TranscriptMinuteSegment,
  TranscriptRow
} from "./ui-types";

const stablePart = (value: string) =>
  encodeURIComponent(value).replaceAll("%", "_");

/**
 * Read-only presentation projection. Canonical rows and exact timestamps stay
 * untouched; a cross-boundary segment belongs to the minute in which it starts.
 */
export function transcriptMinuteBlocks(
  rows: readonly TranscriptRow[]
): TranscriptMinuteBlock[] {
  const blocks: TranscriptMinuteBlock[] = [];
  const byKey = new Map<string, TranscriptMinuteBlock>();

  for (const row of rows) {
    if (!row.text.trim()) continue;
    const sessionId = row.sessionId ?? `session-${row.sessionOrdinal ?? 1}`;
    const sessionOrdinal = row.sessionOrdinal ?? 1;
    const minuteIndex = Math.floor(Math.max(0, row.startMs) / 60_000);
    const key = `${sessionId}:${minuteIndex}`;
    let block = byKey.get(key);
    if (!block) {
      block = {
        id: `minute-${stablePart(sessionId)}-${minuteIndex}`,
        line: blocks.length + 1,
        sessionId,
        sessionOrdinal,
        minuteIndex,
        startMs: row.startMs,
        endMs: row.endMs,
        segments: []
      };
      byKey.set(key, block);
      blocks.push(block);
    }
    const { line: _sourceLine, ...source } = row;
    const segment: TranscriptMinuteSegment = { ...source, nodeId: row.id };
    block.segments.push(segment);
    block.startMs = Math.min(block.startMs, row.startMs);
    block.endMs = Math.max(block.endMs, row.endMs);
  }

  return blocks;
}

export function minuteLineMap(rows: readonly TranscriptRow[]): Map<string, number> {
  return new Map(
    transcriptMinuteBlocks(rows).flatMap((block) =>
      block.segments.map((segment) => [segment.nodeId, block.line] as const)
    )
  );
}
